import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useDebouncedSearchQuery } from "../../../../hooks/useDebouncedSearchQuery";
import {
  SPORT_SPECIFIC_OPTIONS,
  isLeagueSelected,
  isSportSelected,
  toggleLeague,
  toggleSport,
  type SportLeagueOption,
  type SportOption,
} from "../createSeasonData";

import styles from "./SportSpecificSelect.module.css";

export type SportSpecificSelectProps = {
  /** Flat list of selected sport ids (see the selection helpers in
   *  `createSeasonData.ts`). Ships as the task's `sport_id` rule. */
  sports: ReadonlyArray<string>;
  /** Flat list of selected league / tournament ids. Ships as the task's
   *  `league_id` rule. Independent of `sports`. */
  leagues: ReadonlyArray<string>;
  onChangeSports: (next: ReadonlyArray<string>) => void;
  onChangeLeagues: (next: ReadonlyArray<string>) => void;
  /** Read-only preview (modal view mode) — the trigger renders disabled and
   *  the panel can't be opened. */
  disabled?: boolean;
  /** Catalogue to render (sports, each with its nested leagues). Defaults to
   *  `SPORT_SPECIFIC_OPTIONS`; the parent passes the backend-derived list.
   *  Injectable so the list can be unit-tested against a small fixture. */
  options?: ReadonlyArray<SportOption>;
  "data-testid"?: string;
};

const filterByLabel = <T extends { label: string }>(
  items: ReadonlyArray<T>,
  query: string,
): ReadonlyArray<T> => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return items;
  return items.filter((item) => item.label.toLowerCase().includes(normalized));
};

/**
 * Multi-select search fields for the "Sport Specific" task condition. Renders
 * the sport catalogue as a sport search picker plus a dependent league search
 * picker. Selections are reported through `onChangeSports` (a flat sport-id
 * array, the task's `sport_id` rule) and `onChangeLeagues` (a flat
 * tournament-id array, the task's `league_id` rule).
 */
const SportSpecificSelect: FC<SportSpecificSelectProps> = ({
  sports,
  leagues,
  onChangeSports,
  onChangeLeagues,
  disabled = false,
  options = SPORT_SPECIFIC_OPTIONS,
  ...rest
}) => {
  const testId = rest["data-testid"] ?? "sport-specific-select";
  const rootRef = useRef<HTMLDivElement>(null);
  const [sportsOpen, setSportsOpen] = useState(false);
  const [leaguesOpen, setLeaguesOpen] = useState(false);
  const [sportsSearch, setSportsSearch] = useState("");
  const [leaguesSearch, setLeaguesSearch] = useState("");

  const { effectiveSearchQuery: sportsSearchQuery } = useDebouncedSearchQuery(
    sportsSearch,
    { minLength: 1 },
  );
  const { effectiveSearchQuery: leaguesSearchQuery } = useDebouncedSearchQuery(
    leaguesSearch,
    { minLength: 1 },
  );

  const closeSportsPanel = useCallback((): void => {
    setSportsOpen(false);
    setSportsSearch("");
  }, []);

  const closeLeaguesPanel = useCallback((): void => {
    setLeaguesOpen(false);
    setLeaguesSearch("");
  }, []);

  useEffect(() => {
    if (!sportsOpen && !leaguesOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeSportsPanel();
        closeLeaguesPanel();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [sportsOpen, leaguesOpen, closeSportsPanel, closeLeaguesPanel]);

  useEffect(() => {
    if (!sportsOpen && !leaguesOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      closeSportsPanel();
      closeLeaguesPanel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sportsOpen, leaguesOpen, closeSportsPanel, closeLeaguesPanel]);

  const selectedSportLabels = useMemo(
    () =>
      options
        .filter((sport) => sports.includes(sport.value))
        .map((sport) => sport.label),
    [options, sports],
  );

  const leagueOptions = useMemo(() => {
    const seen = new Set<string>();
    return options
      .filter((sport) => sports.includes(sport.value))
      .flatMap((sport) => sport.leagues)
      .filter((league) => {
        if (seen.has(league.value)) return false;
        seen.add(league.value);
        return true;
      });
  }, [options, sports]);

  const filteredSportOptions = useMemo(
    () => filterByLabel(options, sportsSearchQuery),
    [options, sportsSearchQuery],
  );

  const filteredLeagueOptions = useMemo(
    () => filterByLabel(leagueOptions, leaguesSearchQuery),
    [leagueOptions, leaguesSearchQuery],
  );

  const selectedLeagueLabels = useMemo(() => {
    const labels: string[] = [];
    const seen = new Set<string>();
    for (const sport of options) {
      for (const league of sport.leagues) {
        if (!leagues.includes(league.value) || seen.has(league.value)) continue;
        seen.add(league.value);
        labels.push(league.label);
      }
    }
    return labels;
  }, [options, leagues]);

  const sportTriggerLabel =
    selectedSportLabels.length > 0
      ? selectedSportLabels.join(", ")
      : AppStrings.CREATE_TASK_SPORT_SPECIFIC_PLACEHOLDER;

  const leagueTriggerLabel =
    selectedLeagueLabels.length > 0
      ? selectedLeagueLabels.join(", ")
      : AppStrings.CREATE_TASK_LEAGUE_PLACEHOLDER;

  const sportsSearchDisplayValue =
    sportsOpen || sportsSearch.length > 0
      ? sportsSearch
      : selectedSportLabels.length > 0
        ? sportTriggerLabel
        : "";

  const leaguesSearchDisplayValue =
    leaguesOpen || leaguesSearch.length > 0
      ? leaguesSearch
      : selectedLeagueLabels.length > 0
        ? leagueTriggerLabel
        : "";

  const handleSportToggle = (sport: SportOption): void => {
    const nextSports = toggleSport(sports, sport);
    const allowedLeagues = new Set(
      options
        .filter((option) => nextSports.includes(option.value))
        .flatMap((option) => option.leagues.map((league) => league.value)),
    );
    const nextLeagues = leagues.filter((league) => allowedLeagues.has(league));
    onChangeSports(nextSports);
    if (nextLeagues.length !== leagues.length) {
      onChangeLeagues(nextLeagues);
    }
    if (nextSports.length === 0) {
      closeLeaguesPanel();
    }
  };

  const renderSportOptions = (panelTestId: string) => (
    <div
      className={styles.panel}
      role="group"
      aria-label={AppStrings.CREATE_TASK_SPORT_SPECIFIC}
      data-testid={panelTestId}
    >
      {filteredSportOptions.length === 0 ? (
        <div className={styles.emptyRow} data-testid={`${panelTestId}-empty`}>
          {AppStrings.CREATE_TASK_PICKER_NO_RESULTS}
        </div>
      ) : (
        filteredSportOptions.map((sport) => (
          <div key={sport.value} className={styles.optionRow}>
            <label className={styles.optionLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isSportSelected(sports, sport)}
                onChange={() => handleSportToggle(sport)}
                data-testid={`${testId}-sport-${sport.value}`}
              />
              <span>{sport.label}</span>
            </label>
          </div>
        ))
      )}
    </div>
  );

  const renderLeagueOptions = (panelTestId: string) => (
    <div
      className={styles.panel}
      role="group"
      aria-label={AppStrings.CREATE_TASK_LEAGUE}
      data-testid={panelTestId}
    >
      {filteredLeagueOptions.length === 0 ? (
        <div className={styles.emptyRow} data-testid={`${panelTestId}-empty`}>
          {AppStrings.CREATE_TASK_PICKER_NO_RESULTS}
        </div>
      ) : (
        filteredLeagueOptions.map((league) => (
          <div key={league.value} className={styles.optionRow}>
            <label className={styles.optionLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isLeagueSelected(leagues, league)}
                onChange={() =>
                  onChangeLeagues(toggleLeague(leagues, league))
                }
                data-testid={`${testId}-league-${league.value}`}
              />
              <span>{league.label}</span>
            </label>
          </div>
        ))
      )}
    </div>
  );

  const showLeagueDropdown =
    leagueOptions.length > 0 || (disabled && selectedLeagueLabels.length > 0);

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      <div className={styles.fieldRow}>
        <div className={styles.selectWrap}>
          <div
            className={`${styles.triggerWrap} ${
              sportsOpen ? styles.triggerWrapOpen : ""
            }`}
          >
            <input
              type="text"
              className={`${styles.searchInput} ${
                !sportsOpen &&
                sportsSearch.length === 0 &&
                selectedSportLabels.length > 0
                  ? styles.searchInputSelectionSummary
                  : ""
              } ${disabled ? styles.searchInputSelectionSummary : ""}`}
              value={disabled ? sportTriggerLabel : sportsSearchDisplayValue}
              placeholder={
                AppStrings.CREATE_TASK_SPORT_SPECIFIC_SEARCH_PLACEHOLDER
              }
              onChange={(event) => {
                setSportsSearch(event.target.value);
                if (!sportsOpen) setSportsOpen(true);
              }}
              onFocus={() => !disabled && setSportsOpen(true)}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={sportsOpen}
              data-testid={`${testId}-search-input`}
            />
            {!disabled && (
              <button
                type="button"
                className={styles.chevronButton}
                onClick={() => {
                  if (sportsOpen) closeSportsPanel();
                  else setSportsOpen(true);
                }}
                tabIndex={-1}
                aria-label={AppStrings.CREATE_TASK_SPORT_SPECIFIC}
                data-testid={`${testId}-search-toggle`}
              >
                {sportsOpen ? "\u2303" : "\u2304"}
              </button>
            )}
          </div>

          {sportsOpen &&
            !disabled &&
            renderSportOptions(`${testId}-search-panel`)}
        </div>
      </div>

      {showLeagueDropdown && (
        <div className={styles.leagueField}>
          <label
            className={styles.fieldLabel}
            htmlFor={`${testId}-league-search-input`}
          >
            {AppStrings.CREATE_TASK_LEAGUE}
          </label>
          <div className={styles.fieldRow}>
            <div className={styles.selectWrap}>
              <div
                className={`${styles.triggerWrap} ${
                  leaguesOpen ? styles.triggerWrapOpen : ""
                }`}
              >
                <input
                  id={`${testId}-league-search-input`}
                  type="text"
                  className={`${styles.searchInput} ${
                    !leaguesOpen &&
                    leaguesSearch.length === 0 &&
                    selectedLeagueLabels.length > 0
                      ? styles.searchInputSelectionSummary
                      : ""
                  } ${disabled ? styles.searchInputSelectionSummary : ""}`}
                  value={
                    disabled ? leagueTriggerLabel : leaguesSearchDisplayValue
                  }
                  placeholder={AppStrings.CREATE_TASK_LEAGUE_SEARCH_PLACEHOLDER}
                  onChange={(event) => {
                    setLeaguesSearch(event.target.value);
                    if (!leaguesOpen) setLeaguesOpen(true);
                  }}
                  onFocus={() => !disabled && setLeaguesOpen(true)}
                  disabled={disabled || leagueOptions.length === 0}
                  aria-haspopup="listbox"
                  aria-expanded={leaguesOpen}
                  data-testid={`${testId}-league-search-input`}
                />
                {!disabled && leagueOptions.length > 0 && (
                  <button
                    type="button"
                    className={styles.chevronButton}
                    onClick={() => {
                      if (leaguesOpen) closeLeaguesPanel();
                      else setLeaguesOpen(true);
                    }}
                    tabIndex={-1}
                    aria-label={AppStrings.CREATE_TASK_LEAGUE}
                    data-testid={`${testId}-league-search-toggle`}
                  >
                    {leaguesOpen ? "\u2303" : "\u2304"}
                  </button>
                )}
              </div>

              {leaguesOpen &&
                !disabled &&
                renderLeagueOptions(`${testId}-league-search-panel`)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SportSpecificSelect;
